import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Popover, Table } from "antd";
import {
  useGetMyTaskExecutionsCountQuery,
  useGetMyTaskExecutionsQuery,
} from "shared/api";
import { buildMyTaskExecutionRows } from "shared/helpers";
import { useMyTaskExecutionsColumns } from "shared/hooks";
import { Routes } from "shared/routers";
import { MyTaskExecutionRow } from "shared/types";
import { CompleteTaskModal } from "../CompleteTaskModal";

/**
 * antd puts a popover at 1030 by default — above the modal (1000) and its
 * notifications (1010). The popup stays open behind the complete-task modal,
 * so it has to sit below both instead.
 */
const POPOVER_Z_INDEX = 900;

interface MaintenanceWidgetProps {
  /**
   * Rendered size of the icon in px. The page header uses the full-size icon;
   * the denser production header passes a smaller one.
   */
  iconSize?: number;
}

export const MaintenanceWidget = ({
  iconSize = 70,
}: MaintenanceWidgetProps) => {
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const navigate = useNavigate();

  const onCompleteTask = (taskExecutionId: number) => {
    setSelectedId(taskExecutionId);
    setIsCompleteModalOpen(true);
  };

  const columns = useMyTaskExecutionsColumns({ onCompleteTask });

  // The modal renders outside the popover, so clicking it (or pressing Escape)
  // would otherwise dismiss the popover underneath.
  const onOpenChange = (next: boolean) => {
    if (!next && isCompleteModalOpen) return;
    setOpen(next);
  };

  const goToMyTasks = () => {
    setOpen(false);
    navigate(Routes.taskHubUserCompleteTasks.url);
  };

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  const { data: countData } = useGetMyTaskExecutionsCountQuery();
  const count = countData?.count_my_task_executions ?? 0;

  // Only fetch the (heavier) tree while the dropdown is open.
  const { data: summaryData, isFetching } = useGetMyTaskExecutionsQuery(
    undefined,
    { skip: !open }
  );

  // Render the root's children directly: the virtual root (name === null) holds
  // top-level tasks in `task_executions` and named sections in `subsections`.
  const rows = useMemo<MyTaskExecutionRow[]>(() => {
    const root = summaryData?.my_task_executions;
    return root ? buildMyTaskExecutionRows(root) : [];
  }, [summaryData]);

  const content = (
    <div className="w-[1200px] max-w-[96vw]">
      <div className="max-h-[70vh] overflow-y-auto">
        <Table<MyTaskExecutionRow>
          size="small"
          columns={columns}
          dataSource={rows}
          loading={isFetching}
          pagination={false}
          expandable={{ defaultExpandAllRows: true }}
        />
      </div>

      <div className="mt-3 flex justify-end">
        <Button
          type="primary"
          style={{ backgroundColor: themeColor, borderColor: themeColor }}
          onClick={goToMyTasks}
        >
          Go to my tasks
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Popover
        trigger="click"
        placement="bottomRight"
        open={open}
        onOpenChange={onOpenChange}
        content={content}
        zIndex={POPOVER_Z_INDEX}
      >
        <div
          className="cursor-pointer relative flex items-center justify-center"
          role="button"
          tabIndex={0}
        >
          {count > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-[20px] px-[6px] text-xs bg-red-600 text-white rounded-full flex items-center justify-center leading-none z-10 border border-white">
              {count > 99 ? "99+" : count}
            </span>
          )}

          <img
            width={iconSize}
            height={iconSize}
            className="object-contain hover:opacity-80 transition-opacity"
            src="/product-card-maintance-icon.png"
            alt="Maintenance"
          />
        </div>
      </Popover>

      {/*
       * Completing invalidates the MY_SUMMARY/MY_COUNT tags, so the tree above
       * and the badge refetch on their own once the modal closes.
       */}
      <CompleteTaskModal
        open={isCompleteModalOpen}
        taskExecutionId={selectedId}
        onClose={() => setIsCompleteModalOpen(false)}
      />
    </>
  );
};
